import { useState, useEffect } from "react";
import { Sparkles, Save, FileDown, Mail, Loader2, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";

const CRITICAL_BAD = ["missing", "disputed"];

const computePreview = (schema, answers) => {
  let total = 0;
  const cats = {};
  for (const cat of schema) {
    let pts = 0;
    for (const q of cat.questions) {
      const opt = q.options.find((o) => o.value === answers[q.id]);
      if (opt) pts += opt.points;
    }
    const cs = cat.questions.length ? pts / cat.questions.length : 0;
    cats[cat.id] = Math.round(cs * 10) / 10;
    total += cs * cat.weight;
  }
  let score = Math.round(total * 10);
  const critical = CRITICAL_BAD.includes(answers.cd_mother_deed) || CRITICAL_BAD.includes(answers.cd_ec);
  if (critical) score = Math.min(score, 290);
  return { score, cats, critical };
};

const AiFindings = ({ ai }) => (
  <div data-testid="assessment-ai-findings" className="bg-[#F9FAFA] border border-[#113835]/10 p-4 space-y-2">
    <p className="tt-label !mb-1">AI Findings {ai.language_detected ? `· ${ai.language_detected}` : ""} {ai.confidence ? `· confidence: ${ai.confidence}` : ""}</p>
    {(ai.key_findings || []).slice(0, 6).map((f, idx) => (
      <p key={f.slice(0, 40) + idx} className="text-xs text-gray-600">• {f}</p>
    ))}
    {(ai.red_flags || []).map((f, idx) => (
      <p key={f.slice(0, 40) + idx} className="text-xs text-red-700 font-medium">⚑ {f}</p>
    ))}
  </div>
);

const QuestionGroup = ({ cat, answers, catScore, onChange }) => (
  <div>
    <div className="flex items-center justify-between border-b border-[#C1A74A]/40 pb-2 mb-4">
      <p className="font-heading text-sm font-medium text-[#113835]">{cat.label}</p>
      <p className="text-[0.65rem] text-gray-400 uppercase tracking-widest">
        {cat.weight === 0 ? "Deal-breaker gate" : `${Math.round(cat.weight * 100)}%`} · {catScore}/100
      </p>
    </div>
    <div className="space-y-4">
      {cat.questions.map((q) => (
        <div key={q.id}>
          <label className="tt-label !normal-case !tracking-normal !text-[0.72rem]" htmlFor={`aq-${q.id}`}>{q.label}</label>
          <select
            id={`aq-${q.id}`}
            data-testid={`assessment-q-${q.id}`}
            className="tt-input !py-2 text-sm"
            value={answers[q.id] || ""}
            onChange={(e) => onChange(q.id, e.target.value)}
          >
            <option value="" disabled>Select finding</option>
            {q.options.map((o) => (
              <option key={o.value} value={o.value}>{o.label} ({o.points} pts)</option>
            ))}
          </select>
        </div>
      ))}
    </div>
  </div>
);

export const AssessmentPanel = ({ item, onSaved }) => {
  const [schema, setSchema] = useState(null);
  const [answers, setAnswers] = useState(item.assessment?.answers || {});
  const [notes, setNotes] = useState(item.assessment?.summary_notes || "");
  const [ai, setAi] = useState(item.ai_analysis || null);
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [saved, setSaved] = useState(item.assessment || null);

  useEffect(() => {
    api.get("/admin/assessment-schema").then(({ data }) => setSchema(data)).catch(() => {});
  }, []);

  if (!schema) return null;
  const preview = computePreview(schema, answers);
  const answeredCount = Object.keys(answers).length;
  const totalQuestions = schema.reduce((n, c) => n + c.questions.length, 0);

  const runAi = async () => {
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/admin/inquiries/${item.id}/ai-analyze`);
      setAi(data);
      if (data.suggested_answers) setAnswers((prev) => ({ ...prev, ...data.suggested_answers }));
      toast.success(`AI analysis complete (${Object.keys(data.suggested_answers || {}).length} answers suggested)`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "AI analysis failed");
    } finally {
      setAnalyzing(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/inquiries/${item.id}/assessment`, {
        answers, summary_notes: notes,
      });
      setSaved(data);
      toast.success(`Assessment saved — Terraa Score ${data.terraa_score}/1000`);
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/admin/inquiries/${item.id}/report.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Terraa-Score-Report-TT-${item.id.slice(0, 8).toUpperCase()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download report");
    }
  };

  const emailReport = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/admin/inquiries/${item.id}/send-report`);
      toast.success(`Report emailed to ${data.sent_to}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Email failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="assessment-panel" className="border border-[#C1A74A]/60">
      <div className="bg-[#113835] px-6 py-4 flex items-center justify-between">
        <div>
          <p className="font-heading text-xs tracking-[0.2em] uppercase text-[#C1A74A]">Terraa Score Assessment</p>
          <p className="text-[0.65rem] text-white/60 mt-1">Due diligence rating out of 1000</p>
        </div>
        {saved && (
          <div className="text-right" data-testid="assessment-score">
            <p className="font-heading text-2xl font-semibold text-[#F4D387]">{saved.terraa_score}<span className="text-sm text-white/60"> /1000</span></p>
            <p className="text-[0.65rem] uppercase tracking-widest" style={{ color: saved.grade.color }}>{saved.grade.name}</p>
          </div>
        )}
      </div>

      <div className="p-6 space-y-6">
        <button
          data-testid="assessment-ai-analyze"
          onClick={runAi}
          disabled={analyzing || !(item.document_ids?.length)}
          className="tt-btn-gold !py-2.5 !px-5 w-full justify-center disabled:opacity-50"
        >
          {analyzing ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} strokeWidth={1.5} />}
          {analyzing ? "Reading documents (vernacular OCR)..." : "AI Analyze Uploaded Documents"}
        </button>
        {!item.document_ids?.length && (
          <p className="text-[0.7rem] text-gray-400 -mt-4">No documents uploaded — answer the questionnaire manually.</p>
        )}

        {ai && <AiFindings ai={ai} />}

        {preview.critical && (
          <div data-testid="assessment-critical-alert" className="bg-[#dc2626] text-white px-4 py-3">
            <p className="font-heading text-xs font-bold uppercase tracking-wide">High Risk: Critical Documents Missing</p>
            <p className="text-[0.7rem] font-light mt-1">Mother Deed / EC missing or disputed — Terraa Score is capped below 300/1000.</p>
          </div>
        )}

        {schema.map((cat) => (
          <QuestionGroup key={cat.id} cat={cat} answers={answers} catScore={preview.cats[cat.id]}
            onChange={(qid, v) => setAnswers({ ...answers, [qid]: v })} />
        ))}

        <div>
          <label className="tt-label" htmlFor="assessment-notes">Examiner's Notes</label>
          <textarea id="assessment-notes" data-testid="assessment-notes" className="tt-input" value={notes}
            onChange={(e) => setNotes(e.target.value)} placeholder="Summary observations for the report..." />
        </div>

        <div className="bg-[#F9FAFA] border border-[#113835]/10 px-4 py-3 flex items-center justify-between">
          <p className="text-xs text-gray-500">{answeredCount}/{totalQuestions} factors assessed</p>
          <p className="font-heading text-lg font-semibold text-[#113835]" data-testid="assessment-live-preview">
            {preview.score} <span className="text-xs text-gray-400 font-normal">/1000 preview</span>
          </p>
        </div>

        <button data-testid="assessment-save" onClick={save} disabled={saving || answeredCount === 0}
          className="tt-btn-primary w-full justify-center !py-3 disabled:opacity-50">
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} strokeWidth={1.5} />}
          Save Assessment
        </button>

        {saved && (
          <div className="grid grid-cols-2 gap-3">
            <button data-testid="assessment-download-pdf" onClick={downloadPdf} className="tt-btn-outline !py-2.5 justify-center">
              <FileDown size={15} strokeWidth={1.5} /> Download PDF
            </button>
            <button data-testid="assessment-email-report" onClick={emailReport} disabled={sending}
              className="tt-btn-outline !py-2.5 justify-center disabled:opacity-50">
              {sending ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} strokeWidth={1.5} />}
              Email to Client
            </button>
          </div>
        )}

        {saved && (
          <button
            data-testid="assessment-copy-public-link"
            onClick={() => {
              navigator.clipboard.writeText(`${window.location.origin}/report/${item.id}`);
              toast.success("Public rating link copied — share it with the client to sell the report");
            }}
            className="w-full text-[0.7rem] uppercase tracking-[0.14em] font-heading text-[#1D504C] hover:text-[#C1A74A] transition-colors flex items-center justify-center gap-2"
          >
            <LinkIcon size={13} strokeWidth={1.5} /> Copy Public Rating Link (client pays to download PDF)
          </button>
        )}
      </div>
    </div>
  );
};
